import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import { fetch_payout_detail, post_payout_detail_approve, post_payout_detail_reject } from '../api';
import {
  REASON_MAX_LEN,
  approve_done_text,
  detail_view,
  reject_done_text,
  reject_reason_error,
  review_error
} from './adapters';
import { approve_body, reject_body } from './requests';
import useIfscLookup from './use_ifsc_lookup';
import useReveal from './use_reveal';

/*
 * PROD-7828 A-22 W2 — one submission next to the details in use (masked), changed fields highlighted,
 * the holder-name hint, the shared-account warning, approve or reject. Nobody edits a submission.
 * props: detail_uuid, on_close(), on_reviewed(text) after a review landed, on_stale() when the list is
 * out of date (409 not pending / 404), on_error(e, fallback) for a 401 only (the shell's sign-in path).
 */
const LOAD_FAILED_TEXT = 'Could not load the payment details. Try again.';
const REVIEW_FAILED_TEXT = 'The review may not have been saved. Reload the list to check.';

const RevealLine = ({ detail_uuid }) => {
  const { number, busy, error, reveal, hide } = useReveal(detail_uuid);
  return (
    <div className="tp-pd-reveal-wrap">
      {number ? (
        <div className="tp-pd-reveal" role="status">
          <span className="tp-mono tp-strong">{number}</span>
          <span className="tp-hint">Hidden again after 30 seconds. This view was recorded.</span>
          <button type="button" className="tp-link" onClick={hide}>
            Hide now
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="tp-btn-out"
          onClick={busy ? undefined : reveal}
          aria-disabled={busy ? 'true' : undefined}
        >
          {busy ? 'Loading…' : 'Show full account number'}
        </button>
      )}
      {error ? (
        <span className="tp-neg" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
};

const FieldsTable = ({ view, ifsc_hint }) => (
  <div className="tp-tablewrap tp-pd-fields">
    <table className="tp-table">
      <thead>
        <tr>
          <th>Field</th>
          <th>Submitted</th>
          {view.has_current ? <th>In use now</th> : null}
        </tr>
      </thead>
      <tbody>
        {view.rows.map((r) => (
          <tr key={r.key} className={r.changed ? 'tp-pd-changed' : undefined}>
            <td>
              {r.label}
              {r.changed ? <span className="tp-pd-changed-tag"> Changed</span> : null}
            </td>
            <td>
              {r.submission}
              {r.key === 'ifsc' && ifsc_hint ? (
                <div className={`tp-hint${ifsc_hint.warn ? ' tp-neg' : ''}`}>{ifsc_hint.text}</div>
              ) : null}
            </td>
            {view.has_current ? <td>{r.current}</td> : null}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ReviewDialog = ({ detail_uuid, on_close, on_reviewed, on_stale, on_error }) => {
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [view, setView] = useState(null);
  const [load_error, setLoadError] = useState('');
  const [confirm_shared, setConfirmShared] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [action_error, setActionError] = useState('');
  const [stale, setStale] = useState(false);
  const seq = useRef(0);
  const ifsc_hint = useIfscLookup(view ? view.ifsc : '');

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setStatus('loading');
    fetch_payout_detail(detail_uuid)
      .then((d) => {
        if (mine !== seq.current) return;
        setView(detail_view(d));
        setStatus('ready');
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        const v = review_error(e, LOAD_FAILED_TEXT);
        if (v.kind === 'auth') on_error(e, LOAD_FAILED_TEXT);
        if (v.kind === 'not_found') setStale(true);
        setLoadError(v.text);
        setStatus('error');
      });
  }, [detail_uuid, on_error]);

  useEffect(() => {
    load();
  }, [load]);

  const shared = view ? view.shared_with.length > 0 : false;
  const reason_err = reject_reason_error(reason);

  const fail = (e) => {
    const v = review_error(e, REVIEW_FAILED_TEXT);
    setSubmitting(false);
    setActionError(v.text);
    if (v.kind === 'auth') on_error(e, REVIEW_FAILED_TEXT);
    if (v.kind === 'stale' || v.kind === 'not_found') setStale(true);
    // The account became shared after this view loaded: re-read it so the warning and checkbox show.
    if (v.kind === 'shared') {
      setConfirmShared(false);
      load();
    }
  };

  const approve = () => {
    if (submitting || stale || (shared && !confirm_shared)) return;
    setSubmitting(true);
    setActionError('');
    post_payout_detail_approve(approve_body(view.detail_uuid, shared && confirm_shared))
      .then((d) => {
        setSubmitting(false);
        on_reviewed(approve_done_text(d, view.teacher.teacher_name));
      })
      .catch(fail);
  };

  const reject = () => {
    setTouched(true);
    if (submitting || stale || reason_err) return;
    setSubmitting(true);
    setActionError('');
    post_payout_detail_reject(reject_body(view.detail_uuid, reason))
      .then(() => {
        setSubmitting(false);
        on_reviewed(reject_done_text(view.teacher.teacher_name));
      })
      .catch(fail);
  };

  const off = submitting || stale;
  const approve_off = off || (shared && !confirm_shared);

  return (
    <TpDialog
      title="Review payment details"
      on_close={on_close}
      busy={submitting}
      guard_text={discard_guard_text(reason)}
    >
      <div className="tp-modal tp-modal-wide tp-pd">
        {status === 'loading' ? <CustomLoading /> : null}
        {status === 'error' && !stale ? <LoadFailed text={load_error} on_retry={load} /> : null}
        {stale ? (
          <div className="tp-banner tp-banner-warn" role="alert">
            {action_error || load_error}{' '}
            <button type="button" className="tp-link" onClick={on_stale}>
              Reload the list
            </button>
          </div>
        ) : null}
        {status === 'ready' && view ? (
          <>
            <div className="tp-pd-head">
              <div>
                <div className="tp-strong">{view.teacher.teacher_name}</div>
                <div className="tp-hint">{view.teacher.teacher_email}</div>
              </div>
              <div className="tp-pd-pills">
                <StatusPill tone={view.status.tone}>{view.status.label}</StatusPill>
                <StatusPill tone={view.name_match.tone}>{view.name_match.label}</StatusPill>
              </div>
            </div>
            <div className="tp-hint">
              Submitted {view.submitted_at} IST.{' '}
              {view.has_current
                ? `This replaces the details in use now; ${view.changed_count} ${
                    view.changed_count === 1 ? 'field has' : 'fields have'
                  } changed.`
                : 'This is the first set of details from this teacher.'}
            </div>
            {view.review_note ? (
              <div className="tp-ctx">Reason given to the teacher: {view.review_note}</div>
            ) : null}

            {shared ? (
              <div className="tp-banner tp-banner-warn" role="alert">
                This account number is already approved for{' '}
                {view.shared_with.map((t) => t.teacher_name).join(', ')}. Check with the teacher
                before approving.
              </div>
            ) : null}

            <FieldsTable view={view} ifsc_hint={ifsc_hint} />
            <RevealLine detail_uuid={view.detail_uuid} />

            {view.can_review ? (
              <>
                {shared ? (
                  <label className="tp-pd-check" htmlFor="tp-pd-confirm-shared">
                    <input
                      id="tp-pd-confirm-shared"
                      type="checkbox"
                      checked={confirm_shared}
                      onChange={(e) => setConfirmShared(e.target.checked)}
                      disabled={off}
                    />
                    I have checked that this teacher should be paid into this shared account.
                  </label>
                ) : null}
                {rejecting ? (
                  <div className="tp-field">
                    <label htmlFor="tp-pd-reason">
                      Reason for rejecting (the teacher sees this)
                    </label>
                    <textarea
                      id="tp-pd-reason"
                      className="tp-textarea"
                      value={reason}
                      maxLength={REASON_MAX_LEN * 2}
                      onChange={(e) => setReason(e.target.value)}
                      onBlur={() => setTouched(true)}
                      placeholder="For example: the IFSC belongs to a closed branch."
                    />
                    {touched && reason_err ? (
                      <span className="tp-neg" role="alert">
                        {reason_err}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {action_error && !stale ? (
                  <div className="tp-neg" role="alert">
                    {action_error}
                  </div>
                ) : null}
                <div className="tp-modal-ft">
                  {rejecting ? (
                    <>
                      <button
                        type="button"
                        className="tp-btn-ghost"
                        onClick={submitting ? undefined : () => setRejecting(false)}
                        aria-disabled={submitting ? 'true' : undefined}
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        className="tp-btn tp-btn-danger"
                        onClick={off ? undefined : reject}
                        aria-disabled={off ? 'true' : undefined}
                      >
                        {submitting ? 'Saving…' : 'Reject details'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="tp-btn-out"
                        onClick={off ? undefined : () => setRejecting(true)}
                        aria-disabled={off ? 'true' : undefined}
                      >
                        Reject…
                      </button>
                      <button
                        type="button"
                        className="tp-btn"
                        onClick={approve_off ? undefined : approve}
                        aria-disabled={approve_off ? 'true' : undefined}
                      >
                        {submitting ? 'Saving…' : 'Approve details'}
                      </button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="tp-ctx">
                These details are no longer waiting for review, so they cannot be approved or
                rejected.
              </div>
            )}
          </>
        ) : null}
      </div>
    </TpDialog>
  );
};

export default ReviewDialog;
